import Dexie from "dexie";
import { fs } from "../firebase.ts";
import { Bookmark, Bookmark2 } from "../models/Bookmark.ts";
import { version } from "../../package.json";
import { HeartBank } from "./HeartBank.ts";

import {
    collection,
    doc,
    addDoc,
    getDocs,
    updateDoc,
    deleteDoc,
    query,
    where,
    orderBy,
    limit,
    CollectionReference,
    DocumentData,
    onSnapshot
} from "firebase/firestore";

export class Bookmarks extends Dexie {
    private heartbank: HeartBank = new HeartBank();
    private bookmarks: Dexie.Table<Bookmark, number>;
    private collection: CollectionReference<DocumentData>;
    private unsubscribe?: () => void;

    constructor() {
        super("Bookmarks");
        this.version(parseInt(version))
            .stores({
                bookmarks: "++id, date, book, page, [book+page]"
            })
            .upgrade((tx) => {
                // TODO: later delete old version of dexie
                // return tx
                //     .table("bookmarks")
                //     .toCollection()
                //     .modify((bookmark) => {
                //         bookmark.date = new Date();
                //     });
            });
        this.bookmarks = this.table("bookmarks");
        this.collection = collection(fs, "bookmarks");
    }

    async getRecent(): Promise<{ book: number; page: number } | null> {
        const user = await this.heartbank.waitForAuth2();
        return user ? this.getRecentBookmark(user.uid) : this.getRecent2();
    }

    async get(book: number): Promise<number | null> {
        const user = await this.heartbank.waitForAuth2();
        return user ? this.getBookmark(user.uid, book) : this.get2(book);
    }

    async update(book: number, page: number): Promise<number | string> {
        const user = await this.heartbank.waitForAuth2();
        return user
            ? this.updateBookmark(user.uid, book, page)
            : this.update2(book, page);
    }

    async remove(book: number, page: number) {
        const user = await this.heartbank.waitForAuth2();
        return user
            ? this.removeBookmark(user.uid, book, page)
            : this.remove2(book, page);
    }

    // dexie

    async getRecent2(): Promise<{ book: number; page: number } | null> {
        const bookmark = await this.bookmarks.orderBy("date").last();
        return bookmark ? { book: bookmark.book, page: bookmark.page } : null;
    }

    async get2(book: number): Promise<number | null> {
        const bookmark = await this.bookmarks
            .where("book")
            .equals(book)
            .first();
        return bookmark ? bookmark.page : null;
    }

    async update2(book: number, page: number): Promise<number> {
        const bookmark = await this.bookmarks
            .where("book")
            .equals(book)
            .first();

        if (bookmark) {
            bookmark.date = new Date();
            bookmark.page = page;
            return this.bookmarks.update(bookmark.id!, bookmark);
        } else {
            return this.bookmarks.add({ date: new Date(), book, page });
        }
    }

    async remove2(book: number, page: number) {
        const bookmark = await this.bookmarks
            .where("[book+page]")
            .equals([book, page])
            .first();
        return this.bookmarks.delete(bookmark!.id!);
    }

    // firestore

    async getRecentBookmark(
        uid: string
    ): Promise<{ book: number; page: number } | null> {
        const q = query(
            this.collection,
            where("uid", "==", uid),
            orderBy("date", "desc"),
            limit(1)
        );
        const querySnapshot = await getDocs(q);
        const bookmark = querySnapshot.docs[0]?.data();
        return bookmark ? { book: bookmark.book, page: bookmark.page } : null;
    }

    async getBookmark(uid: string, book: number): Promise<number | null> {
        const q = query(
            this.collection,
            where("uid", "==", uid),
            where("book", "==", book)
        );
        const querySnapshot = await getDocs(q);
        const bookmark = querySnapshot.docs[0]?.data();
        return bookmark?.page ?? null;
    }

    async updateBookmark(
        uid: string,
        book: number,
        page: number
    ): Promise<string> {
        const q = query(
            this.collection,
            where("uid", "==", uid),
            where("book", "==", book)
        );
        const querySnapshot = await getDocs(q);

        if (querySnapshot.empty) {
            const docRef = await addDoc(this.collection, {
                uid,
                date: new Date(),
                book,
                page
            });
            return docRef.id;
        } else {
            const docSnap = querySnapshot.docs[0];
            const docRef = doc(fs, "bookmarks", docSnap.id);
            await updateDoc(docRef, {
                date: new Date(),
                page
            });
            return docSnap.id;
        }
    }

    async removeBookmark(uid: string, book: number, page: number) {
        const q = query(
            this.collection,
            where("uid", "==", uid),
            where("book", "==", book),
            where("page", "==", page)
        );
        const querySnapshot = await getDocs(q);
        const docSnap = querySnapshot.docs[0];
        const docRef = doc(fs, "bookmarks", docSnap.id);
        await deleteDoc(docRef);
    }

    async setupRealtimeListener(
        uid: string,
        book: number,
        callback: (page: number) => void
    ) {
        const q = query(
            this.collection,
            where("uid", "==", uid),
            where("book", "==", book)
        );
        const querySnapshot = await getDocs(q);

        if (!querySnapshot.empty) {
            const docSnap = querySnapshot.docs[0];
            const docRef = doc(fs, "bookmarks", docSnap.id);
            if (this.unsubscribe) this.unsubscribe();

            const unsubscribe = onSnapshot(
                docRef,
                (docSnap) => {
                    if (docSnap.exists()) {
                        const bookmark = {
                            uid: docSnap.id,
                            ...docSnap.data()
                        } as Bookmark2;
                        callback(bookmark.page);
                    }
                },
                (error) => {
                    console.error(
                        "Error listening to document: ",
                        error.message
                    );
                }
            );
            this.unsubscribe = unsubscribe;
        }
    }
}
